import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Post } from '../lib/api';
import { MdEditor } from '../mdeditor';
import { PhotoUploader } from '../photos';
import { usePhotos } from '../usePhotos';
import { plural } from '../lib/domain';
import { useMe, useToast } from '../hooks';
import { ErrorBox, Loading, PageHead } from '../ui';
import { useBarTitle } from '../barTitle';

export default function PostForm() {
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  useBarTitle(editId ? 'Mitteilung bearbeiten' : 'An alle schreiben'); // also while loading
  const posts = useQuery({ queryKey: ['posts'], queryFn: () => api<Post[]>('/posts'), enabled: !!editId });
  if (posts.error) return <ErrorBox error={posts.error} />;
  if (editId && !posts.data) return <Loading />;
  const p = editId ? posts.data?.find(x => x.id === editId) : undefined;
  if (editId && !p) return <ErrorBox error={new Error('Mitteilung nicht gefunden.')} />;
  return <PostFormBody key={editId ?? 'new'} p={p} />;
}

function PostFormBody({ p }: { p?: Post }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const [title, setTitle] = useState(p?.title ?? '');
  const [body, setBody] = useState(p?.body ?? '');
  const [pinned, setPinned] = useState(p?.pinned ?? false);
  const [notify, setNotify] = useState(true);
  const photos = usePhotos(p?.images);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const admin = me?.role === 'admin';

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    if (title.trim().length < 2) return setError('Bitte einen Betreff angeben.');
    if (!body.trim()) return setError('Bitte eine Nachricht schreiben.');
    if (photos.uploading) return setError('Einen Moment – die Bilder werden noch hochgeladen.');
    const images = photos.payload();
    setBusy(true);
    try {
      if (p) {
        await api(`/posts/${p.id}`, {
          method: 'PATCH',
          body: { title: title.trim(), body, images, ...(admin ? { pinned } : {}) },
        });
        toast('Mitteilung gespeichert (ohne neue E-Mail)');
      } else {
        const r = await api<{ notified: boolean; recipients: number }>('/posts', {
          method: 'POST',
          body: { title: title.trim(), body, images, notify, ...(admin ? { pinned } : {}) },
        });
        toast(
          !notify
            ? 'Mitteilung veröffentlicht'
            : r.notified
              ? `Mitteilung verschickt an ${plural(r.recipients, 'Mitglied', 'Mitglieder')}`
              : 'Mitteilung veröffentlicht – E-Mail übersprungen (zu viele in der letzten Stunde)',
        );
      }
      void qc.invalidateQueries({ queryKey: ['posts'] });
      void nav('/mitteilungen', { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead title={p ? 'Mitteilung bearbeiten' : 'An alle schreiben'} />
      <form className="form panel" onSubmit={e => void submit(e)} noValidate style={{ maxWidth: 820 }}>
        <label className="fl">
          <span>Betreff</span>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            maxLength={100}
            placeholder="z. B. Hallenbad am Montag geschlossen"
          />
        </label>
        <MdEditor id="post-body" label="Nachricht" value={body} onChange={setBody} rows={9} maxLength={10_000} />
        <PhotoUploader photos={photos} label="Bilder (bis zu 6, kommen auch in die E-Mail)" />
        {admin && (
          <label className="set-row" style={{ border: 0, padding: 0, cursor: 'pointer' }}>
            <div>
              <b>Als wichtig anheften</b>
              <p>Bleibt oben auf der Pinnwand.</p>
            </div>
            <span className="switch">
              <input
                type="checkbox"
                checked={pinned}
                onChange={e => setPinned(e.target.checked)}
                aria-label="Als wichtig anheften"
              />
              <span />
            </span>
          </label>
        )}
        {!p && (
          <div className="notify-box">
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', fontWeight: 600 }}>
              <span className="switch">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={e => setNotify(e.target.checked)}
                  aria-label="An alle schicken (E-Mail und Push)"
                />
                <span />
              </span>
              An alle schicken (E-Mail und Push)
            </label>
            <p>An alle Mitglieder, die Mitteilungen per E-Mail oder Push wollen.</p>
          </div>
        )}
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void nav(-1)}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy || photos.uploading}>
            {p ? 'Speichern' : notify ? 'Senden' : 'Veröffentlichen'}
          </button>
        </div>
      </form>
    </>
  );
}
